import { makeStyles } from "tss-react/mui"
import { IconButton, Stack, Typography } from "@mui/material"

import { useApiQuery, usePublicApiQuery } from "../../../../../../core/api/useApiQuery"
import { PackageSlipSkeleton } from "./slipModal/PackageSlipSkeleton"
import { Box } from "@mui/system"
import {
    fetchMeetingServiceOrdersForExport,
    MeetingServiceOrdersForExportResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../meetingServiceApi"
import SingleOrder from "./slipModal/SingleOrder"
import { useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
    fetchOrderDetails,
    fetchPublicOrderDetails,
    MeetingServiceQuery,
    OrderDetailsResponse,
} from "../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { getPublicPackageSlipReceiptRoute, LOGIN_ROUTE } from "../../../../../../constants/routes"
import { SharePrintButtons } from "../../../../../nonAdminUser/reports/MeetingsReceipt/components/SharePrintButtons"

import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { ErrorView } from "../../../../../../shared/components/errors/ErrorView"
import GeneralErrorAnim from "../../../../../../shared/assets/general-error-lottie-anim.json"
import FacilitynetLogo from "../../../../../../shared/layouts/assets/facilit-net-logo.svg"
import { MeetingServiceOrder } from "../meetingServiceTypes"
import { CalendarEvent } from "../../../../../../shared/components/calendar/calendarTypes"
import { useEnvironment } from "../../../../../environment/envHooks"
import { ReceiptAndSlipDialogHeader } from "../../../components/receiptAndSlip/ReceiptAndSlipDialogHeader"

const useStyles = makeStyles()((theme) => ({
    container: {
        backgroundColor: theme.palette.tailwind.slate[100],
        minHeight: "100vh",
        gap: "3.5rem",
        paddingBottom: "3.5rem",
        justifyContent: "start",
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
        "@media print": {
            backgroundColor: "transparent",
            minHeight: "auto",
            width: "100%",
            paddingBottom: 0,
        },
    },
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        marginTop: "1.5rem",
        [theme.breakpoints.down("sm")]: {
            marginTop: 0,
        },
        "@media print": {
            marginTop: 0,
        },
    },
    slide: {
        width: "80%",
        "@media print": {
            width: "100%",
            display: "block",
        },
    },
    header: {
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", // L | C | R
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1.5rem",
        position: "sticky",
        top: 0,
        backgroundColor: "white",
        boxShadow: "0px 2px 6px 0px #00000014",
        "@media print": {
            display: "none",
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            padding: "0 1.5rem",
            zIndex: 1,
            boxShadow: "0px 3px 1px -2px #00000033,  0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        },
    },
    carouselContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
    },
    carouselText: {
        fontSize: "1rem",
    },
    logo: {
        width: "auto",
    },
    logoLink: {
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.up("sm")]: {
            display: "none",
        },
    },
    actionButtons: {
        justifySelf: "end",
    },
    navigationContainer: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
}))

export type AnyMeetingServiceOrder = MeetingServiceOrder | CalendarEvent

type StartPageLayoutProps = Readonly<{
    id?: number
    merchantId?: number
    onClose?: () => void
    orderIds?: number[]
    open?: boolean
    orders?: AnyMeetingServiceOrder[]
}>

const transformOrdersForExport = (data: MeetingServiceOrdersForExportResponse) => data?.orders
const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)

const getIndexOfPreviewedOrder = (orders?: AnyMeetingServiceOrder[], id?: number) => {
    if (!id || !orders?.length) return 0
    return orders.findIndex((o) => o.id === id)
}

export function OrderPackageSlip({
    id,
    open = false,
    merchantId,
    orderIds,
    orders = [],
    onClose = () => {},
}: StartPageLayoutProps) {
    const { classes } = useStyles()
    const { slipUid: uid } = useParams()
    const [currentMeetingIndex, setCurrentMeetingIndex] = useState<number>(getIndexOfPreviewedOrder(orders, id))
    const { currentEnv } = useEnvironment()

    const userId = useAppSelector(selectEffectiveUserId) || 0
    const activeOrder = useMemo(() => orders?.[currentMeetingIndex], [orders, currentMeetingIndex])
    const isAuthenticated = Boolean(userId)
    const isPublic = Boolean(uid)
    const isSelectMode = Boolean(orderIds?.length)

    const {
        data: exportOrders = [],
        isFetching: isFetchingOrdersForExport,
        error: isFetchingOrdersForExportError,
    } = useApiQuery(fetchMeetingServiceOrdersForExport, {
        queryName: MerchantMeetingServiceOrdersQuery.ORDERS_FOR_EXPORT,
        dependencies: {
            merchantId: merchantId ?? 0,
            orderIds: orderIds,
        },
        enabled: isAuthenticated && Boolean(orderIds?.length),
        select: transformOrdersForExport,
    })

    const {
        data: orderDetailsPublic,
        isFetching: orderDetailsPublicLoading,
        error: orderDetailsPublicError,
    } = usePublicApiQuery(fetchPublicOrderDetails, {
        queryName: MeetingServiceQuery.PUBLIC_ORDER_DETAILS,
        dependencies: {
            orderUid: uid ?? "",
        },
        enabled: Boolean(uid) && isPublic && !isSelectMode,
        select: transformData,
    })

    const {
        data: orderDetailsAuth,
        isFetching: orderDetailsAuthLoading,
        isError: orderDetailsAuthError,
    } = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: {
            orderId: Number(activeOrder?.id) || 0,
        },
        enabled: Boolean(activeOrder?.id) && !isPublic && !isSelectMode,
        select: transformData,
    })

    const goToPreviousOrder = () => {
        if (currentMeetingIndex > 0) {
            const prevIndex = currentMeetingIndex - 1
            setCurrentMeetingIndex(prevIndex)
        }
    }

    const goToNextOrder = () => {
        if (currentMeetingIndex < orders.length - 1) {
            const nextIndex = currentMeetingIndex + 1
            setCurrentMeetingIndex(nextIndex)
        }
    }

    const handleOrderChange = (event: any) => {
        const selectedId = event.target.value
        const index = getIndexOfPreviewedOrder(orders, selectedId)
        setCurrentMeetingIndex(index)
    }

    const orderDetails = orderDetailsAuth ?? orderDetailsPublic
    const isLoading = orderDetailsPublicLoading || orderDetailsAuthLoading || isFetchingOrdersForExport
    const isError = orderDetailsPublicError || orderDetailsAuthError || isFetchingOrdersForExportError

    const skeletonCount = isSelectMode ? (orderIds?.length ?? 1) : 1

    const slipContent = useMemo(() => {
        if (isLoading) {
            return Array.from({ length: skeletonCount }).map((_, index) => <PackageSlipSkeleton key={index} />)
        }

        if (isSelectMode) {
            return exportOrders.map((order) => <SingleOrder key={order.id} order={order} />)
        }

        if (orderDetails) {
            return <SingleOrder order={orderDetails} />
        }

        return null
    }, [isLoading, skeletonCount, isSelectMode, exportOrders, orderDetails])

    const content = isError ? (
        <ErrorView
            lottieAnimation={GeneralErrorAnim}
            title="A problem occurred"
            message="Failed to load"
            buttonLabel="Close"
            onClose={onClose}
        />
    ) : (
        <Stack direction="column" className={classes.container}>
            <Box className={classes.header}>
                <ReceiptAndSlipDialogHeader
                    handleOrderChange={handleOrderChange}
                    id={Number(activeOrder?.id)}
                    uid={uid}
                    isAuthenticated={isAuthenticated}
                    isSelectMode={isSelectMode}
                    orderIdsLength={orderIds?.length}
                    orders={orders}
                    orderDetails={orderDetails}
                    variant="slip"
                />
                <Link className={classes.logoLink} to={LOGIN_ROUTE}>
                    <img className={classes.logo} src={FacilitynetLogo} alt="Facilitynet" />
                </Link>

                <Box className={classes.navigationContainer}>
                    {isAuthenticated && !isSelectMode && (
                        <Box className={classes.carouselContainer}>
                            <IconButton onClick={goToPreviousOrder} disabled={currentMeetingIndex === 0}>
                                <ChevronLeft />
                            </IconButton>
                            <Typography className={classes.carouselText}>
                                {currentMeetingIndex + 1}/{orders.length}
                            </Typography>
                            <IconButton onClick={goToNextOrder} disabled={currentMeetingIndex === orders.length - 1}>
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    )}
                </Box>

                <Box className={classes.actionButtons}>
                    <SharePrintButtons
                        onClose={onClose}
                        hasShare={!isSelectMode && !isPublic}
                        hasClose={true}
                        copyUrl={`${currentEnv?.webapp?.baseUrl}${getPublicPackageSlipReceiptRoute(orderDetails?.uid)}`}
                    />
                </Box>
            </Box>
            <Box id="printable-content" className={classes.slideContainer}>
                <Stack direction="column" spacing="2rem" className={classes.slide}>
                    {slipContent}
                </Stack>
            </Box>
        </Stack>
    )

    return isPublic ? (
        content
    ) : (
        <StandardDialog open={open} onClose={onClose} isFullScreen hideCloseIcon topbar={<></>}>
            {content}
        </StandardDialog>
    )
}
